import { useState, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useUserProfile } from '@/hooks/useUserProfile';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Building2, User, Calendar, Shield, Mail, Phone, Globe, CheckCircle2, Upload, MapPin, Users, Link, BarChart3, Target, Headphones, Crown, RefreshCw, Bell, Gift, Key } from 'lucide-react';
import { useSettings } from '@/hooks/useSettings';
import { Switch } from '@/components/ui/switch';
import { useTranslation } from '@/contexts/LanguageContext';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { z } from 'zod';
import { PlanManagementDialog } from '@/components/plans/PlanManagementDialog';
import { maskCNPJ, maskPhone, maskCEP } from '@/utils/inputMasks';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { TeamManagement } from '@/components/TeamManagement';
import { usePlanBonuses } from '@/hooks/usePlanBonuses';
import { changePasswordSchema } from '@/utils/passwordValidation';
import { TwoFactorEnableDialog } from '@/components/TwoFactorEnableDialog';

const profileSchema = z.object({
  full_name: z.string().trim().min(3, { message: "Nome deve ter no mínimo 3 caracteres" }).max(100),
  phone: z.string().trim().max(20).optional().or(z.literal('')),
});

const tenantSchema = z.object({
  name: z.string().trim().min(3, { message: "Nome da empresa deve ter no mínimo 3 caracteres" }).max(100),
  domain: z.string().trim().max(100).optional().or(z.literal('')),
  cnpj: z.string().trim().max(18).optional().or(z.literal('')),
  phone: z.string().trim().max(20).optional().or(z.literal('')),
  email: z.string().trim().email({ message: "E-mail inválido" }).max(100).optional().or(z.literal('')),
  address_street: z.string().trim().max(200).optional().or(z.literal('')),
  address_number: z.string().trim().max(20).optional().or(z.literal('')),
  address_complement: z.string().trim().max(100).optional().or(z.literal('')),
  address_neighborhood: z.string().trim().max(100).optional().or(z.literal('')),
  address_city: z.string().trim().max(100).optional().or(z.literal('')),
  address_state: z.string().trim().max(2).optional().or(z.literal('')),
  address_zipcode: z.string().trim().max(10).optional().or(z.literal('')),
});

export default function Account() {
  const { user } = useAuth();
  const { profile, tenant, currentPlan, primaryRole, isSuperAdmin, isAdmin, updateProfile, updateTenant, uploadAvatar, uploadCompanyLogo, isLoading } = useUserProfile();
  const { settings, updateSettings, isLoading: loadingSettings } = useSettings();
  const { t } = useTranslation();
  const { activeBonus } = usePlanBonuses(tenant?.id);
  
  const [editingProfile, setEditingProfile] = useState(false);
  const [editingTenant, setEditingTenant] = useState(false);
  const [profileData, setProfileData] = useState({ full_name: '', phone: '' });
  const [showPlanDialog, setShowPlanDialog] = useState(false);
  const [tenantData, setTenantData] = useState({ 
    name: '', 
    domain: '', 
    cnpj: '', 
    phone: '', 
    email: '', 
    address_street: '', 
    address_number: '', 
    address_complement: '', 
    address_neighborhood: '', 
    address_city: '', 
    address_state: '', 
    address_zipcode: '' 
  });

  useEffect(() => {
    document.title = 'Dashi | Conta';
  }, []);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSyncing, setIsSyncing] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordErrors, setPasswordErrors] = useState<Record<string, string>>({});
  const [show2FADialog, setShow2FADialog] = useState(false);

  const handleToggle = async (field: string, value: boolean) => {
    await updateSettings.mutateAsync({ [field]: value });
  };

  const getUserInitials = () => {
    if (!profile?.full_name) return "U";
    const names = profile.full_name.split(" ");
    return names.length > 1 
      ? `${names[0][0]}${names[names.length - 1][0]}`.toUpperCase()
      : names[0][0].toUpperCase();
  };

  const getRoleBadge = (role: string) => {
    const badges = {
      super_admin: { label: 'Super Admin', variant: 'destructive' as const },
      admin: { label: 'Administrador', variant: 'default' as const },
      employee: { label: 'Funcionário', variant: 'secondary' as const },
    };
    return badges[role as keyof typeof badges] || badges.employee;
  };

  const startEditProfile = () => {
    setProfileData({
      full_name: profile?.full_name || '',
      phone: profile?.phone || '',
    });
    setAvatarPreview(profile?.avatar_url || null);
    setEditingProfile(true);
    setErrors({});
  };

  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setAvatarFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setAvatarPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const startEditTenant = () => {
    setTenantData({
      name: tenant?.name || '',
      domain: tenant?.domain || '',
      cnpj: tenant?.cnpj || '',
      phone: tenant?.phone || '',
      email: tenant?.email || '',
      address_street: tenant?.address_street || '',
      address_number: tenant?.address_number || '',
      address_complement: tenant?.address_complement || '',
      address_neighborhood: tenant?.address_neighborhood || '',
      address_city: tenant?.address_city || '',
      address_state: tenant?.address_state || '',
      address_zipcode: tenant?.address_zipcode || '',
    });
    setLogoPreview(tenant?.logo_url || null);
    setEditingTenant(true);
    setErrors({});
  };

  const handleSaveProfile = async () => {
    try {
      const validated = profileSchema.parse(profileData);
      
      let avatarUrl = profile?.avatar_url;
      if (avatarFile && user?.id) {
        try {
          avatarUrl = await uploadAvatar(avatarFile, user.id);
        } catch (error) {
          console.error("Error uploading avatar:", error);
        }
      }

      await updateProfile.mutateAsync({
        ...validated,
        avatar_url: avatarUrl,
      });
      setEditingProfile(false);
      setAvatarFile(null);
    } catch (error) {
      if (error instanceof z.ZodError) {
        const fieldErrors: Record<string, string> = {};
        error.errors.forEach((err) => {
          if (err.path[0]) fieldErrors[err.path[0] as string] = err.message;
        });
        setErrors(fieldErrors);
      }
    }
  };

  const handleSaveTenant = async () => {
    try {
      const validated = tenantSchema.parse(tenantData);
      
      let logoUrl = tenant?.logo_url;
      if (logoFile && tenant?.id) {
        try {
          logoUrl = await uploadCompanyLogo(logoFile, tenant.id);
        } catch (error) {
          console.error("Error uploading logo:", error);
        }
      }

      await updateTenant.mutateAsync({
        ...validated,
        logo_url: logoUrl,
      });
      setEditingTenant(false);
      setLogoFile(null);
    } catch (error) {
      if (error instanceof z.ZodError) {
        const fieldErrors: Record<string, string> = {};
        error.errors.forEach((err) => {
          if (err.path[0]) fieldErrors[err.path[0] as string] = err.message;
        });
        setErrors(fieldErrors);
      }
    }
  };

  const handleSyncSubscription = async () => {
    setIsSyncing(true);
    try {
      // Get user's last checkout session
      // For now, we'll just show a message that manual sync requires a session_id
      toast({
        title: "Sincronização Manual",
        description: "Para sincronizar manualmente, você precisa do ID da sessão de checkout. Entre em contato com o suporte se necessário.",
        variant: "default",
      });
    } catch (error: any) {
      toast({
        title: "Erro ao sincronizar",
        description: error.message || "Não foi possível sincronizar o plano.",
        variant: "destructive",
      });
    } finally {
      setIsSyncing(false);
    }
  };

  const handleChangePassword = async () => {
    setPasswordErrors({});
    
    try {
      const validated = changePasswordSchema.parse(passwordData);
      
      // Verificar senha atual fazendo login temporário
      const { error: loginError } = await supabase.auth.signInWithPassword({
        email: user!.email!,
        password: validated.currentPassword
      });
      
      if (loginError) {
        setPasswordErrors({ currentPassword: 'Senha atual incorreta' });
        return;
      }
      
      // Atualizar senha
      const { error: updateError } = await supabase.auth.updateUser({
        password: validated.newPassword
      });
      
      if (updateError) {
        toast({
          title: "Erro ao alterar senha",
          description: updateError.message,
          variant: "destructive",
        });
        return;
      }
      
      toast({
        title: "Senha alterada com sucesso!",
        description: "Sua senha foi atualizada.",
      });
      
      // Limpar formulário
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      setChangingPassword(false);
      
    } catch (error) {
      if (error instanceof z.ZodError) {
        const fieldErrors: Record<string, string> = {};
        error.errors.forEach((err) => {
          if (err.path[0]) fieldErrors[err.path[0] as string] = err.message;
        });
        setPasswordErrors(fieldErrors);
      }
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold flex items-center gap-2">
          <User className="h-8 w-8" />
          Minha Conta
        </h1>
        <p className="text-muted-foreground mt-1">Gerencie suas informações pessoais e da empresa</p>
      </div>

      <Tabs defaultValue="company" className="w-full">
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="company">
            <Building2 className="h-4 w-4 mr-2" />
            Informações da Empresa
          </TabsTrigger>
          <TabsTrigger value="personal">
            <User className="h-4 w-4 mr-2" />
            Informações Pessoais
          </TabsTrigger>
          <TabsTrigger value="notifications">
            <Bell className="h-4 w-4 mr-2" />
            Notificações
          </TabsTrigger>
          <TabsTrigger value="security">
            <Shield className="h-4 w-4 mr-2" />
            Segurança
          </TabsTrigger>
          <TabsTrigger value="plan">
            Minha Assinatura
          </TabsTrigger>
        </TabsList>

        {/* Informações da Empresa */}
        <TabsContent value="company" className="space-y-4">
          {(isAdmin || isSuperAdmin) && tenant ? (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Building2 className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <CardTitle>Informações da Empresa</CardTitle>
                      <CardDescription>Dados da sua organização no Dashi</CardDescription>
                    </div>
                  </div>
                  {!editingTenant && isAdmin && (
                    <Button onClick={startEditTenant} variant="outline">
                      Editar
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                {editingTenant ? (
                  <>
                    {/* Logo Upload */}
                    <div className="space-y-3">
                      <Label>Logo da Empresa</Label>
                      <div className="flex items-center gap-4">
                        <Avatar className="h-24 w-24 rounded-lg">
                          {logoPreview ? (
                            <AvatarImage src={logoPreview} className="object-cover" />
                          ) : (
                            <AvatarFallback className="rounded-lg bg-muted">
                              <Building2 className="h-12 w-12 text-muted-foreground" />
                            </AvatarFallback>
                          )}
                        </Avatar>
                        <div className="flex-1">
                          <Input
                            type="file"
                            accept="image/*"
                            onChange={handleLogoChange}
                            className="cursor-pointer"
                          />
                          <p className="text-xs text-muted-foreground mt-1">
                            Recomendado: imagem quadrada (PNG ou JPG)
                          </p>
                        </div>
                      </div>
                    </div>

                    <Separator />

                    {/* Dados Básicos */}
                    <div className="space-y-4">
                      <h3 className="font-medium">Dados Básicos</h3>
                      <div className="grid gap-4 md:grid-cols-2">
                        <div className="space-y-2">
                          <Label htmlFor="company_name">Nome da Empresa *</Label>
                          <Input
                            id="company_name"
                            value={tenantData.name}
                            onChange={(e) => setTenantData({ ...tenantData, name: e.target.value })}
                          />
                          {errors.name && <p className="text-sm text-destructive">{errors.name}</p>}
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="cnpj">CNPJ</Label>
                          <Input
                            id="cnpj"
                            value={tenantData.cnpj}
                            onChange={(e) => {
                              const masked = maskCNPJ(e.target.value);
                              setTenantData({ ...tenantData, cnpj: masked });
                            }}
                            placeholder="00.000.000/0000-00"
                            maxLength={18}
                          />
                          {errors.cnpj && <p className="text-sm text-destructive">{errors.cnpj}</p>}
                        </div>
                      </div>
                    </div>

                    <Separator />

                    {/* Contato */}
                    <div className="space-y-4">
                      <h3 className="font-medium">Contato</h3>
                      <div className="grid gap-4 md:grid-cols-2">
                        <div className="space-y-2">
                          <Label htmlFor="company_email">E-mail</Label>
                          <Input
                            id="company_email"
                            type="email"
                            value={tenantData.email}
                            onChange={(e) => setTenantData({ ...tenantData, email: e.target.value })}
                            placeholder="contato@empresa.com"
                            pattern="[^\s@]+@[^\s@]+\.[^\s@]+"
                          />
                          {errors.email && <p className="text-sm text-destructive">{errors.email}</p>}
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="company_phone">Telefone</Label>
                          <Input
                            id="company_phone"
                            value={tenantData.phone}
                            onChange={(e) => {
                              const masked = maskPhone(e.target.value);
                              setTenantData({ ...tenantData, phone: masked });
                            }}
                            placeholder="(00)00000-0000"
                            maxLength={14}
                          />
                          {errors.phone && <p className="text-sm text-destructive">{errors.phone}</p>}
                        </div>
                        <div className="space-y-2 md:col-span-2">
                          <Label htmlFor="domain">Domínio</Label>
                          <Input
                            id="domain"
                            value={tenantData.domain}
                            onChange={(e) => setTenantData({ ...tenantData, domain: e.target.value })}
                            placeholder="exemplo.com"
                          />
                          {errors.domain && <p className="text-sm text-destructive">{errors.domain}</p>}
                        </div>
                      </div>
                    </div>

                    <Separator />

                    {/* Endereço */}
                    <div className="space-y-4">
                      <h3 className="font-medium flex items-center gap-2">
                        <MapPin className="h-4 w-4" />
                        Endereço
                      </h3>
                      <div className="grid gap-4 md:grid-cols-6">
                        <div className="space-y-2 md:col-span-2">
                          <Label htmlFor="zipcode">CEP</Label>
                          <Input
                            id="zipcode"
                            value={tenantData.address_zipcode}
                            onChange={(e) => {
                              const masked = maskCEP(e.target.value);
                              setTenantData({ ...tenantData, address_zipcode: masked });
                            }}
                            placeholder="00000-000"
                            maxLength={9}
                          />
                        </div>
                        <div className="space-y-2 md:col-span-4">
                          <Label htmlFor="street">Rua/Avenida</Label>
                          <Input
                            id="street"
                            value={tenantData.address_street}
                            onChange={(e) => setTenantData({ ...tenantData, address_street: e.target.value })}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="number">Número</Label>
                          <Input
                            id="number"
                            value={tenantData.address_number}
                            onChange={(e) => setTenantData({ ...tenantData, address_number: e.target.value })}
                          />
                        </div>
                        <div className="space-y-2 md:col-span-2">
                          <Label htmlFor="complement">Complemento</Label>
                          <Input
                            id="complement"
                            value={tenantData.address_complement}
                            onChange={(e) => setTenantData({ ...tenantData, address_complement: e.target.value })}
                          />
                        </div>
                        <div className="space-y-2 md:col-span-3">
                          <Label htmlFor="neighborhood">Bairro</Label>
                          <Input
                            id="neighborhood"
                            value={tenantData.address_neighborhood}
                            onChange={(e) => setTenantData({ ...tenantData, address_neighborhood: e.target.value })}
                          />
                        </div>
                        <div className="space-y-2 md:col-span-4">
                          <Label htmlFor="city">Cidade</Label>
                          <Input
                            id="city"
                            value={tenantData.address_city}
                            onChange={(e) => setTenantData({ ...tenantData, address_city: e.target.value })}
                          />
                        </div>
                        <div className="space-y-2 md:col-span-2">
                          <Label htmlFor="state">Estado (UF)</Label>
                          <Input
                            id="state"
                            value={tenantData.address_state}
                            onChange={(e) => setTenantData({ ...tenantData, address_state: e.target.value.toUpperCase() })}
                            placeholder="SP"
                            maxLength={2}
                          />
                        </div>
                      </div>
                    </div>

                    <div className="flex gap-2 pt-4">
                      <Button onClick={handleSaveTenant} disabled={updateTenant.isPending}>
                        {updateTenant.isPending ? 'Salvando...' : 'Salvar'}
                      </Button>
                      <Button variant="outline" onClick={() => {
                        setEditingTenant(false);
                        setLogoFile(null);
                        setLogoPreview(null);
                      }}>
                        Cancelar
                      </Button>
                    </div>
                  </>
                ) : (
                  <div className="space-y-6">
                    {/* Logo e Dados Básicos */}
                    <div className="flex items-start gap-6">
                      <Avatar className="h-24 w-24 rounded-lg">
                        {tenant.logo_url ? (
                          <AvatarImage src={tenant.logo_url} className="object-cover" />
                        ) : (
                          <AvatarFallback className="rounded-lg bg-muted">
                            <Building2 className="h-12 w-12 text-muted-foreground" />
                          </AvatarFallback>
                        )}
                      </Avatar>
                      <div className="flex-1 grid gap-4 md:grid-cols-2">
                        <div className="space-y-1">
                          <Label className="text-muted-foreground">Nome da Empresa</Label>
                          <p className="font-medium">{tenant.name}</p>
                        </div>
                        {tenant.cnpj && (
                          <div className="space-y-1">
                            <Label className="text-muted-foreground">CNPJ</Label>
                            <p className="font-medium">{tenant.cnpj}</p>
                          </div>
                        )}
                      </div>
                    </div>

                    <Separator />

                    {/* Contato */}
                    <div className="grid gap-4 md:grid-cols-3">
                      {tenant.email && (
                        <div className="space-y-1">
                          <Label className="text-muted-foreground flex items-center gap-2">
                            <Mail className="h-4 w-4" />
                            E-mail
                          </Label>
                          <p className="font-medium">{tenant.email}</p>
                        </div>
                      )}
                      {tenant.phone && (
                        <div className="space-y-1">
                          <Label className="text-muted-foreground flex items-center gap-2">
                            <Phone className="h-4 w-4" />
                            Telefone
                          </Label>
                          <p className="font-medium">{tenant.phone}</p>
                        </div>
                      )}
                      {tenant.domain && (
                        <div className="space-y-1">
                          <Label className="text-muted-foreground flex items-center gap-2">
                            <Globe className="h-4 w-4" />
                            Domínio
                          </Label>
                          <p className="font-medium">{tenant.domain}</p>
                        </div>
                      )}
                    </div>

                    {/* Endereço */}
                    {(tenant.address_street || tenant.address_city) && (
                      <>
                        <Separator />
                        <div className="space-y-3">
                          <Label className="text-muted-foreground flex items-center gap-2">
                            <MapPin className="h-4 w-4" />
                            Endereço
                          </Label>
                          <div className="space-y-1">
                            {tenant.address_street && (
                              <p className="font-medium">
                                {tenant.address_street}
                                {tenant.address_number && `, ${tenant.address_number}`}
                                {tenant.address_complement && ` - ${tenant.address_complement}`}
                              </p>
                            )}
                            {tenant.address_neighborhood && (
                              <p className="text-sm text-muted-foreground">{tenant.address_neighborhood}</p>
                            )}
                            {tenant.address_city && (
                              <p className="text-sm text-muted-foreground">
                                {tenant.address_city}{tenant.address_state && ` - ${tenant.address_state}`}
                                {tenant.address_zipcode && ` • CEP: ${tenant.address_zipcode}`}
                              </p>
                            )}
                          </div>
                        </div>
                      </>
                    )}

                    <Separator />

                    {/* Status e Data de Cadastro */}
                    <div className="grid gap-4 md:grid-cols-2">
                      <div className="space-y-1">
                        <Label className="text-muted-foreground flex items-center gap-2">
                          <Calendar className="h-4 w-4" />
                          Data de Cadastro
                        </Label>
                        <p className="font-medium">
                          {format(new Date(tenant.created_at), "dd 'de' MMMM 'de' yyyy", { locale: ptBR })}
                        </p>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-muted-foreground flex items-center gap-2">
                          <CheckCircle2 className="h-4 w-4" />
                          Status
                        </Label>
                        <Badge variant={tenant.is_active ? "default" : "destructive"}>
                          {tenant.is_active ? 'Ativa' : 'Inativa'}
                        </Badge>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="py-8">
                <p className="text-center text-muted-foreground">
                  Você não tem permissão para visualizar informações da empresa.
                </p>
              </CardContent>
            </Card>
          )}

          {/* Gerenciamento de Usuários (apenas para Admin) */}
          <TeamManagement />
        </TabsContent>

        {/* Informações Pessoais */}
        <TabsContent value="personal" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Avatar className="h-16 w-16">
                    {profile?.avatar_url ? (
                      <AvatarImage src={profile.avatar_url} className="object-cover" />
                    ) : (
                      <AvatarFallback className="bg-primary text-primary-foreground text-xl">
                        {getUserInitials()}
                      </AvatarFallback>
                    )}
                  </Avatar>
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <User className="h-5 w-5" />
                      Informações Pessoais
                    </CardTitle>
                    <CardDescription>Seus dados de cadastro no sistema</CardDescription>
                  </div>
                </div>
                {!editingProfile && (
                  <Button onClick={startEditProfile} variant="outline">
                    Editar
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {editingProfile ? (
                <>
                  {/* Avatar Upload */}
                  <div className="space-y-3">
                    <Label>Foto de Perfil</Label>
                    <div className="flex items-center gap-4">
                      <Avatar className="h-24 w-24">
                        {avatarPreview ? (
                          <AvatarImage src={avatarPreview} className="object-cover" />
                        ) : (
                          <AvatarFallback className="bg-primary text-primary-foreground text-2xl">
                            {getUserInitials()}
                          </AvatarFallback>
                        )}
                      </Avatar>
                      <div className="flex-1">
                        <Input
                          type="file"
                          accept="image/*"
                          onChange={handleAvatarChange}
                          className="cursor-pointer"
                        />
                        <p className="text-xs text-muted-foreground mt-1">
                          Recomendado: imagem quadrada (PNG ou JPG)
                        </p>
                      </div>
                    </div>
                  </div>

                  <Separator />

                  <div className="space-y-2">
                    <Label htmlFor="full_name">Nome Completo</Label>
                    <Input
                      id="full_name"
                      value={profileData.full_name}
                      onChange={(e) => setProfileData({ ...profileData, full_name: e.target.value })}
                    />
                    {errors.full_name && <p className="text-sm text-destructive">{errors.full_name}</p>}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">Telefone</Label>
                    <Input
                      id="phone"
                      value={profileData.phone}
                      onChange={(e) => {
                        const masked = maskPhone(e.target.value);
                        setProfileData({ ...profileData, phone: masked });
                      }}
                      placeholder="(00)00000-0000"
                      maxLength={14}
                    />
                    {errors.phone && <p className="text-sm text-destructive">{errors.phone}</p>}
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={handleSaveProfile} disabled={updateProfile.isPending}>
                      {updateProfile.isPending ? 'Salvando...' : 'Salvar'}
                    </Button>
                    <Button variant="outline" onClick={() => {
                      setEditingProfile(false);
                      setAvatarFile(null);
                      setAvatarPreview(null);
                    }}>
                      Cancelar
                    </Button>
                  </div>
                </>
              ) : (
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-1">
                    <Label className="text-muted-foreground">Nome Completo</Label>
                    <p className="font-medium">{profile?.full_name || 'Não informado'}</p>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-muted-foreground flex items-center gap-2">
                      <Mail className="h-4 w-4" />
                      Email
                    </Label>
                    <p className="font-medium">{user?.email}</p>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-muted-foreground flex items-center gap-2">
                      <Phone className="h-4 w-4" />
                      Telefone
                    </Label>
                    <p className="font-medium">{profile?.phone || 'Não informado'}</p>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-muted-foreground flex items-center gap-2">
                      <Shield className="h-4 w-4" />
                      Nível de Acesso
                    </Label>
                    <Badge variant={getRoleBadge(primaryRole || 'employee').variant}>
                      {getRoleBadge(primaryRole || 'employee').label}
                    </Badge>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Notificações */}
        <TabsContent value="notifications" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>{t('emailNotifications')}</CardTitle>
              <CardDescription>{t('emailNotificationsDescription')}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>{t('generalNotifications')}</Label>
                  <p className="text-sm text-muted-foreground">{t('generalNotificationsDescription')}</p>
                </div>
                <Switch
                  checked={settings?.email_notifications}
                  onCheckedChange={(checked) => handleToggle('email_notifications', checked)}
                />
              </div>
              <Separator />
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>{t('weeklyReport')}</Label>
                  <p className="text-sm text-muted-foreground">{t('weeklyReportDescription')}</p>
                </div>
                <Switch
                  checked={settings?.weekly_report}
                  onCheckedChange={(checked) => handleToggle('weekly_report', checked)}
                />
              </div>
              <Separator />
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>{t('monthlyReport')}</Label>
                  <p className="text-sm text-muted-foreground">{t('monthlyReportDescription')}</p>
                </div>
                <Switch
                  checked={settings?.monthly_report}
                  onCheckedChange={(checked) => handleToggle('monthly_report', checked)}
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>{t('pushNotifications')}</CardTitle>
              <CardDescription>{t('pushNotificationsDescription')}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>{t('enablePush')}</Label>
                  <p className="text-sm text-muted-foreground">{t('enablePushDescription')}</p>
                </div>
                <Switch
                  checked={settings?.push_notifications}
                  onCheckedChange={(checked) => handleToggle('push_notifications', checked)}
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Segurança */}
        <TabsContent value="security" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Shield className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <CardTitle>Segurança da Conta</CardTitle>
                  <CardDescription>Gerencie suas configurações de segurança</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {/* Bloco: Troca de Senha */}
                <div className="space-y-4">
                  <div className="flex items-center gap-2">
                    <Key className="h-5 w-5 text-primary" />
                    <h3 className="text-lg font-semibold">Alterar Senha</h3>
                  </div>
                  
                  <Separator />
                  
                  {!changingPassword ? (
                    <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                      <div>
                        <p className="font-medium">Senha da conta</p>
                        <p className="text-sm text-muted-foreground">
                          Última alteração: {format(new Date(), "dd/MM/yyyy", { locale: ptBR })}
                        </p>
                      </div>
                      <Button onClick={() => setChangingPassword(true)}>
                        Alterar Senha
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-4 p-4 border rounded-lg">
                      <div className="space-y-2">
                        <Label htmlFor="current-password">Senha Atual *</Label>
                        <Input
                          id="current-password"
                          type="password"
                          placeholder="Digite sua senha atual"
                          value={passwordData.currentPassword}
                          onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                        />
                        {passwordErrors.currentPassword && (
                          <p className="text-sm text-destructive">{passwordErrors.currentPassword}</p>
                        )}
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="new-password">Nova Senha *</Label>
                        <Input
                          id="new-password"
                          type="password"
                          placeholder="Digite sua nova senha"
                          value={passwordData.newPassword}
                          onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                        />
                        {passwordErrors.newPassword && (
                          <p className="text-sm text-destructive">{passwordErrors.newPassword}</p>
                        )}
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="confirm-password">Confirmar Nova Senha *</Label>
                        <Input
                          id="confirm-password"
                          type="password"
                          placeholder="Confirme sua nova senha"
                          value={passwordData.confirmPassword}
                          onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                        />
                        {passwordErrors.confirmPassword && (
                          <p className="text-sm text-destructive">{passwordErrors.confirmPassword}</p>
                        )}
                      </div>
                      
                      {/* Requisitos de Senha */}
                      <div className="p-3 bg-blue-50 dark:bg-blue-950 rounded-md text-sm">
                        <p className="font-medium text-blue-900 dark:text-blue-100 mb-2">
                          Requisitos de senha:
                        </p>
                        <ul className="space-y-1 text-blue-700 dark:text-blue-300">
                          <li className="flex items-center gap-2">
                            <CheckCircle2 className="h-4 w-4" />
                            Mínimo de 6 caracteres
                          </li>
                          <li className="flex items-center gap-2">
                            <CheckCircle2 className="h-4 w-4" />
                            Pelo menos 1 letra maiúscula
                          </li>
                          <li className="flex items-center gap-2">
                            <CheckCircle2 className="h-4 w-4" />
                            Pelo menos 1 letra minúscula
                          </li>
                          <li className="flex items-center gap-2">
                            <CheckCircle2 className="h-4 w-4" />
                            Pelo menos 1 número
                          </li>
                        </ul>
                      </div>
                      
                      <div className="flex gap-3 pt-2">
                        <Button
                          variant="outline"
                          className="flex-1"
                          onClick={() => {
                            setChangingPassword(false);
                            setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
                            setPasswordErrors({});
                          }}
                        >
                          Cancelar
                        </Button>
                        <Button
                          className="flex-1"
                          onClick={handleChangePassword}
                        >
                          Salvar Nova Senha
                        </Button>
                      </div>
                      </div>
                    )}
                  </div>

                  {/* Bloco: Autenticação de Dois Fatores */}
                  <div className="space-y-4 mt-8">
                    <div className="flex items-center gap-2">
                      <Shield className="h-5 w-5 text-primary" />
                      <h3 className="text-lg font-semibold">Autenticação de Dois Fatores</h3>
                    </div>
                    
                    <Separator />
                    
                    <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                      <div className="flex-1">
                        <p className="font-medium">Código por E-mail</p>
                        <p className="text-sm text-muted-foreground">
                          Receba um código de 6 dígitos por e-mail sempre que fizer login
                        </p>
                      </div>
                      <Switch
                        checked={settings?.two_factor_enabled}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            // Abrir dialog para validar antes de ativar
                            setShow2FADialog(true);
                          } else {
                            // Desativar diretamente
                            handleToggle('two_factor_enabled', false);
                          }
                        }}
                      />
                    </div>
                    
                    {settings?.two_factor_enabled && (
                      <div className="p-4 bg-green-50 dark:bg-green-950/30 rounded-lg border border-green-200 dark:border-green-800">
                        <div className="flex items-start gap-3">
                          <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400 mt-0.5" />
                          <div className="flex-1">
                            <p className="font-medium text-green-900 dark:text-green-100">
                              2FA Ativado
                            </p>
                            <p className="text-sm text-green-700 dark:text-green-300 mt-1">
                              Sua conta está protegida. Você receberá um código de verificação por e-mail a cada login.
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

        {/* Plano Dashi */}
        <TabsContent value="plan" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Plano Dashi</CardTitle>
              <CardDescription>Informações sobre sua assinatura</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {isLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
              ) : currentPlan ? (
                <>
                  <div className="grid gap-6 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label className="text-muted-foreground">Plano Atual</Label>
                      <div className="flex items-center gap-2">
                        <Badge className="text-base px-3 py-1">
                          <Crown className="h-4 w-4 mr-1" />
                          {currentPlan.name}
                        </Badge>
                        {currentPlan.price_monthly === 0 && currentPlan.price_annual === 0 && (
                          <Badge variant="secondary" className="text-xs">
                            Gratuito
                          </Badge>
                        )}
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-muted-foreground">Status da Assinatura</Label>
                      <div>
                        <Badge variant="default" className="bg-green-500">
                          <CheckCircle2 className="h-3 w-3 mr-1" />
                          Ativa
                        </Badge>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-muted-foreground">Usuários Inclusos</Label>
                      <p className="font-medium">
                        {currentPlan.max_users === 0 ? 'Ilimitado' : `Até ${currentPlan.max_users} ${currentPlan.max_users === 1 ? 'usuário' : 'usuários'}`}
                      </p>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-muted-foreground">Produtos Inclusos</Label>
                      <p className="font-medium">
                        {currentPlan.max_products === 0 ? 'Ilimitado' : `Até ${currentPlan.max_products} ${currentPlan.max_products === 1 ? 'produto' : 'produtos'}`}
                      </p>
                    </div>
                    
                    {!(currentPlan.price_monthly === 0 && currentPlan.price_annual === 0) && (
                      <div className="space-y-2">
                        <Label className="text-muted-foreground">Próximo Vencimento</Label>
                        <p className="font-medium">{format(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000), "dd/MM/yyyy")}</p>
                      </div>
                    )}
                  </div>
                  
                  {/* Bonificação Ativa */}
                  {activeBonus && (
                    <>
                      <Separator />
                      <div className="p-4 bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-950 dark:to-pink-950 rounded-lg border-2 border-purple-200 dark:border-purple-800">
                        <div className="flex items-start gap-3">
                          <Gift className="h-6 w-6 text-purple-600 dark:text-purple-400 mt-1" />
                          <div className="flex-1">
                            <div className="font-semibold text-purple-900 dark:text-purple-100 mb-1">
                              🎉 Plano Bonificado Ativo!
                            </div>
                            <p className="text-sm text-purple-700 dark:text-purple-300">
                              Você está utilizando o plano <strong>{activeBonus.bonus_plan?.name}</strong> como bonificação.
                            </p>
                            <div className="grid grid-cols-2 gap-3 mt-3 text-sm">
                              <div>
                                <Label className="text-purple-600 dark:text-purple-400 text-xs">Válido até</Label>
                                <p className="font-medium text-purple-900 dark:text-purple-100">
                                  {format(new Date(activeBonus.end_date), "dd/MM/yyyy 'às' HH:mm")}
                                </p>
                              </div>
                              <div>
                                <Label className="text-purple-600 dark:text-purple-400 text-xs">Retorna para</Label>
                                <p className="font-medium text-purple-900 dark:text-purple-100">
                                  {activeBonus.return_plan?.name}
                                </p>
                              </div>
                            </div>
                            {activeBonus.notes && (
                              <div className="mt-3 p-2 bg-white/50 dark:bg-black/20 rounded text-xs text-purple-700 dark:text-purple-300">
                                💬 {activeBonus.notes}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                  
                  {/* Informação especial para planos gratuitos */}
                  {currentPlan.price_monthly === 0 && currentPlan.price_annual === 0 && (
                    <>
                      <Separator />
                      <div className="p-4 bg-blue-50 dark:bg-blue-950 rounded-lg border border-blue-200 dark:border-blue-800">
                        <div className="flex items-start gap-3">
                          <Crown className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5" />
                          <div>
                            <div className="font-medium text-blue-900 dark:text-blue-100 mb-1">
                              Plano Gratuito
                            </div>
                            <p className="text-sm text-blue-700 dark:text-blue-300">
                              Você está utilizando o plano gratuito. Para desbloquear recursos avançados, considere fazer upgrade.
                            </p>
                            <Button 
                              variant="link" 
                              className="text-blue-600 dark:text-blue-400 p-0 h-auto mt-2"
                              onClick={() => setShowPlanDialog(true)}
                            >
                              Ver planos disponíveis →
                            </Button>
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                  
                  <Separator />

                  {/* Sync Button */}
                  <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                    <div>
                      <Label className="text-sm font-medium">Sincronizar Plano</Label>
                      <p className="text-xs text-muted-foreground mt-1">
                        Use se o plano não estiver atualizado após o pagamento
                      </p>
                    </div>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={handleSyncSubscription}
                      disabled={isSyncing}
                    >
                      <RefreshCw className={`h-4 w-4 mr-2 ${isSyncing ? 'animate-spin' : ''}`} />
                      {isSyncing ? 'Sincronizando...' : 'Sincronizar'}
                    </Button>
                  </div>
                  
                  <Separator />
                  
                  <div className="space-y-3">
                    <Label className="text-muted-foreground">Recursos Inclusos</Label>
                    <div className="grid gap-2 md:grid-cols-2">
                      {/* Basic Metrics */}
                      {currentPlan?.basic_metrics && (currentPlan.basic_metrics as unknown as string[]).length > 0 && (
                        <div className="flex items-start gap-2">
                          <BarChart3 className="h-4 w-4 text-green-500 mt-0.5" />
                          <div className="text-sm">
                            <div className="font-medium">{(currentPlan.basic_metrics as unknown as string[]).length} Métricas Básicas</div>
                            <div className="text-xs text-muted-foreground">MRR, ARR, Churn, etc.</div>
                          </div>
                        </div>
                      )}
                      
                      {/* Advanced Metrics */}
                      {currentPlan?.advanced_metrics_enabled && (
                        <div className="flex items-start gap-2">
                          <CheckCircle2 className="h-4 w-4 text-green-500 mt-0.5" />
                          <div className="text-sm">
                            <div className="font-medium">Métricas Avançadas</div>
                            <div className="text-xs text-muted-foreground">
                              {currentPlan?.advanced_metrics_mode === 'customizable' 
                                ? `Até ${currentPlan?.max_advanced_metrics} métricas`
                                : `${(currentPlan?.advanced_metrics as unknown as string[])?.length || 0} métricas incluídas`}
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {/* Integrations */}
                      <div className="flex items-start gap-2">
                        <Link className="h-4 w-4 text-green-500 mt-0.5" />
                        <div className="text-sm">
                          <div className="font-medium">Integrações</div>
                          <div className="text-xs text-muted-foreground">
                            {currentPlan?.integrations_mode === 'customizable'
                              ? `Até ${currentPlan?.max_integrations} integrações`
                              : `${(currentPlan?.allowed_integrations as unknown as string[])?.length || 0} pré-definidas`}
                          </div>
                        </div>
                      </div>
                      
                      {/* Goals */}
                      {currentPlan?.goals_enabled && (
                        <div className="flex items-start gap-2">
                          <Target className="h-4 w-4 text-green-500 mt-0.5" />
                          <div className="text-sm">
                            <div className="font-medium">Metas de Negócio</div>
                            <div className="text-xs text-muted-foreground">
                              {currentPlan?.max_goals === 0 ? 'Ilimitadas' : `Até ${currentPlan?.max_goals} metas`}
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {/* Bulk Import */}
                      {currentPlan?.bulk_import_enabled && (
                        <div className="flex items-start gap-2">
                          <Upload className="h-4 w-4 text-green-500 mt-0.5" />
                          <div className="text-sm">
                            <div className="font-medium">Importação em Massa</div>
                            <div className="text-xs text-muted-foreground">Upload de planilhas</div>
                          </div>
                        </div>
                      )}
                      
                      {/* Priority Support */}
                      {currentPlan?.priority_support && (
                        <div className="flex items-start gap-2">
                          <Headphones className="h-4 w-4 text-green-500 mt-0.5" />
                          <div className="text-sm">
                            <div className="font-medium">Suporte Prioritário</div>
                            <div className="text-xs text-muted-foreground">Atendimento preferencial</div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <p className="text-lg font-medium mb-2">Nenhum plano ativo</p>
                  <p className="text-sm mb-4">Escolha um plano para começar a usar o Dashi</p>
                </div>
              )}

              <Separator />

              <div className="flex gap-3 pt-2">
                <Button 
                  variant="default" 
                  className="flex-1"
                  onClick={() => setShowPlanDialog(true)}
                >
                  Gerenciar Plano
                </Button>
                <Button variant="outline" className="flex-1">
                  Histórico de Pagamentos
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Plan Management Dialog */}
      <PlanManagementDialog open={showPlanDialog} onOpenChange={setShowPlanDialog} />
      
      {/* Two Factor Enable Dialog */}
      {user && profile && (
        <TwoFactorEnableDialog
          open={show2FADialog}
          onOpenChange={setShow2FADialog}
          onSuccess={() => {
            updateSettings.mutate({ two_factor_enabled: true });
          }}
          userId={user.id}
          email={user.email || ''}
          userName={profile.full_name || 'Usuário'}
        />
      )}
    </div>
  );
}
